// Centralised online/offline state. The browser's navigator.onLine is
// notoriously unreliable (it goes false on captive portals etc. but never
// reports a flaky server), so we treat it as a hint and fold in our own
// "did the last fetch fail with a network error?" signal.

const listeners = new Set();
let state = navigator.onLine === false ? "offline" : "online";

function emit() {
  for (const fn of listeners) {
    try { fn(state); } catch { /* listener bugs shouldn't cascade */ }
  }
}

function setState(next) {
  if (next === state) return;
  state = next;
  emit();
}

window.addEventListener("online",  () => setState("online"));
window.addEventListener("offline", () => setState("offline"));

export const network = {
  get state() { return state; },
  isOnline()  { return state === "online"; },

  /** Subscribe to state changes. Returns an unsubscribe function. */
  subscribe(fn) {
    listeners.add(fn);
    // Fire immediately so subscribers get the current value.
    try { fn(state); } catch { /* swallow */ }
    return () => listeners.delete(fn);
  },

  /** Called by api.js when a fetch throws TypeError (network error). */
  markOffline() { setState("offline"); },

  /** Called by api.js when a fetch succeeds - proves connectivity. */
  markOnline()  { setState("online"); },
};

// True iff the thrown error looks like a network failure (not a 4xx/5xx).
// `fetch()` throws TypeError on DNS / connection / CORS-network failures.
export function isNetworkError(err) {
  return err instanceof TypeError ||
         /Failed to fetch|NetworkError|Network request failed/i.test(err?.message ?? "");
}
